export type PrinterSnapshot = {
  masterEnabled: boolean;
  targetAgentId: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
  state: 'enabled' | 'disabled';
  hardware: {
    status: 'disabled' | 'ready' | 'offline' | 'faulted';
    queueName: string | null;
    driverName: string | null;
    interfaceSummary: string | null;
    lastSuccessfulStatusCheck: string | null;
    lastError: string | null;
    updatedAt: string | null;
    detail: string;
  };
  transportAvailable: boolean;
  chainResetAvailable: boolean;
  lastReprintableEvent: {
    eventId: string;
    eventType: string;
    displayName: string;
    occurredAt: string;
  } | null;
  targetAgent: PrinterAgent | null;
  availableAgents: PrinterAgent[];
  detail: string;
};

export type PrinterAgent = {
  id: string;
  name: string;
  machineRole: string;
  connected: boolean;
  printerCapability: boolean;
  printerStatus: string | null;
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(init?.body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as { message?: string; error?: string };
    throw new Error(body.message ?? body.error ?? `Request failed with HTTP ${response.status}.`);
  }
  return (await response.json().catch(() => ({}))) as T;
}

export function loadPrinter(): Promise<PrinterSnapshot> {
  return request('/api/v1/printer');
}

export async function setPrinterMasterEnabled(enabled: boolean): Promise<void> {
  await request('/api/v1/printer/master-enabled', { method: 'PUT', body: JSON.stringify({ enabled }) });
}

export async function setPrinterTargetAgent(agentId: string | null): Promise<void> {
  await request('/api/v1/printer/target-agent', { method: 'PUT', body: JSON.stringify({ agentId }) });
}

export function printPrinterTest(): Promise<{ id?: string; status?: string; simulated?: boolean }> {
  return request('/api/v1/printer/test', { method: 'POST', body: JSON.stringify({}) });
}


export type PrinterTestEventKind =
  | 'follow'
  | 'subscription'
  | 'resubscription'
  | 'bits'
  | 'raid'
  | 'gift-subs';

export function printPrinterTestEvent(
  kind: PrinterTestEventKind,
): Promise<{ id?: string; status?: string; simulated?: boolean }> {
  return request('/api/v1/printer/test-event', {
    method: 'POST',
    body: JSON.stringify({ kind }),
  });
}

export function reprintLastPrinterEvent(): Promise<{
  result: { id: string; status: string };
  eventId: string;
  eventType: string;
}> {
  return request('/api/v1/printer/reprint-last-event', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}

export function changedPrinterPaperRoll(): Promise<{
  resetAt: string;
  primed: boolean;
  job: { id: string; status: string };
}> {
  return request('/api/v1/printer/paper-roll-changed', {
    method: 'POST',
    body: JSON.stringify({}),
  });
}
